import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Unified "Today's Activation" — the single source of truth used by home,
 * dashboard, first-gate, mobile shell, and the AI mentor. Backed by the
 * `get_todays_activation()` Postgres function so every surface sees the
 * same row.
 */

export type TodayActivation = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  category: string;
  theme: string | null;
  difficulty: string | null;
  estimated_minutes: number | null;
  tags: string[];
  time_label: string | null;
  duration: string | null;
  breath_pattern: string;
  intention: string;
  instruction: string;
  phrase: string;
  journal: string;
  journal_prompt: string | null;
  meditation: string | null;
  transmission: string | null;
  reflection_question: string | null;
  hero_image_url: string | null;
  wallpaper_url: string | null;
  audio_url: string | null;
  narration_url: string | null;
  video_url: string | null;
  publish_at: string | null;
  activation_number: number | null;
  series: string | null;
  slot: string;
  related_gate_n: number | null;
  related_music_slug: string | null;
  related_book_slug: string | null;
  related_wisdom_slug: string | null;
  related_challenge_slug: string | null;
  related_codex_slug: string | null;
};

function publicClient() {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient(process.env.SUPABASE_URL!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

export const getTodaysActivation = createServerFn({ method: "GET" }).handler(async () => {
  const supa = publicClient();
  const { data, error } = await supa.rpc("get_todays_activation");
  if (error) return null;
  const row = Array.isArray(data) ? data[0] : data;
  return (row ?? null) as TodayActivation | null;
});

export type ArchiveFilters = {
  q?: string;
  tag?: string;
  theme?: string;
  gate?: number;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
};

export const getActivationArchive = createServerFn({ method: "GET" })
  .inputValidator((raw: unknown) => (raw ?? {}) as ArchiveFilters)
  .handler(async ({ data }) => {
    const supa = publicClient();
    const limit = Math.min(Math.max(data.limit ?? 24, 1), 60);
    const offset = Math.max(data.offset ?? 0, 0);
    let q = supa
      .from("activations")
      .select(
        "id, slug, title, subtitle, intention, theme, tags, hero_image_url, publish_at, activation_number, series, slot, estimated_minutes, related_gate_n",
        { count: "exact" },
      )
      .eq("status", "published")
      .lte("publish_at", new Date().toISOString())
      .order("publish_at", { ascending: false, nullsFirst: false })
      .range(offset, offset + limit - 1);
    if (data.q?.trim()) q = q.ilike("title", `%${data.q.trim()}%`);
    if (data.theme?.trim()) q = q.eq("theme", data.theme.trim());
    if (data.tag?.trim()) q = q.contains("tags", [data.tag.trim()]);
    if (data.gate) q = q.eq("related_gate_n", data.gate);
    if (data.from) q = q.gte("publish_at", data.from);
    if (data.to) q = q.lte("publish_at", data.to);
    const { data: rows, count, error } = await q;
    if (error) return { rows: [], count: 0 };
    return { rows: (rows ?? []) as unknown as TodayActivation[], count: count ?? 0 };
  });

export const markActivationSeen = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => raw as { activation_id: string })
  .handler(async ({ data, context }) => {
    if (!data?.activation_id) return { ok: false };
    await context.supabase
      .from("activation_views")
      .upsert(
        { user_id: context.userId, activation_id: data.activation_id },
        { onConflict: "user_id,activation_id", ignoreDuplicates: true },
      );
    return { ok: true };
  });
